import { MeasurementComponent, SubLocation } from "../../shared/enums/Result";
import {
  CablePDMeasurement,
  PanelMeasurement,
  TEVMeasurement,
  UHFMeasurement,
  UltraSonicMeasurement,
  VPISMeasurement,
} from "../../shared/interfaces/Result";
import { ManagedSurveyAssetType, managedSurveyAssetType } from "../../shared/constants/managedSurveyAssetTypes";

/**
 * Components that can appear in results and can have sublocations i.e. plots/positions.
 * This list applies to ALL measurement types.
 */
const plottableComponents = new Set<MeasurementComponent>([
  MeasurementComponent.CableBox,
  MeasurementComponent.Spouts,
  MeasurementComponent.RearLower,
  MeasurementComponent.RearMiddle,
  MeasurementComponent.RearUpper,
  MeasurementComponent.FrontMiddle,
  MeasurementComponent.FrontLower,
  MeasurementComponent.PrimaryCables,
  MeasurementComponent.PrimaryBushing,
  MeasurementComponent.SecondaryBushing1,
  MeasurementComponent.SecondaryCables1,
  MeasurementComponent.SecondaryBushing2,
  MeasurementComponent.SecondaryCables2,
  MeasurementComponent.IncomerCable,
  MeasurementComponent.OverheadSwitch1,
  MeasurementComponent.CircuitBreaker,
  MeasurementComponent.VoltageTransformer,
  MeasurementComponent.CurrentTransformer,
  MeasurementComponent.BusbarSection1,
  MeasurementComponent.LightningArrester,
  MeasurementComponent.OverheadSwitch2,
  MeasurementComponent.BusbarSection2,
]);

/**
 * Asset-type-specific exclusions i.e. if the component doesn't have sublocations for that specific parent asset (applied to ALL measurement types).
 *
 * - If it is a switchgear or GIS switchgear and it is a circuit breaker => undefined
 * - If it is a GIS switchgear and it is a voltage transformer => undefined
 * - If it is a GIS switchgear and it is a current transformer => undefined
 * - If it is a GIS switchgear and it is a lightning arrester => undefined
 */
function shouldExcludeComponentForAssetType(
  componentType: MeasurementComponent,
  assetType?: ManagedSurveyAssetType,
): boolean {
  const isSwitchgearOrGis =
    assetType === managedSurveyAssetType.Switchgear || assetType === managedSurveyAssetType.GisSwitchgear;
  const isGis = assetType === managedSurveyAssetType.GisSwitchgear;

  if (isSwitchgearOrGis && componentType === MeasurementComponent.CircuitBreaker) return true;
  if (isGis && componentType === MeasurementComponent.VoltageTransformer) return true;
  if (isGis && componentType === MeasurementComponent.CurrentTransformer) return true;
  if (isGis && componentType === MeasurementComponent.LightningArrester) return true;

  return false;
}

/**
 * Single shared guard for "should we even try to compute a plot position?"
 * Used by ALL measurement types.
 */
function canHavePlotPosition(
  componentType: MeasurementComponent,
  measurements:
    | TEVMeasurement[]
    | UHFMeasurement[]
    | CablePDMeasurement[]
    | VPISMeasurement[]
    | UltraSonicMeasurement[]
    | undefined,
  assetType?: ManagedSurveyAssetType,
): boolean {
  if (measurements === undefined) return false;
  if (!plottableComponents.has(componentType)) return false;
  if (shouldExcludeComponentForAssetType(componentType, assetType)) return false;

  return true;
}

/**
 * Find the position of the cable box plot for a measurement in a given sublocation.
 *
 * Measurements are saved against a data point ID, which is linked to the capture time. This means there could be measurements against
 * a mix of data point IDs against this result. We use the data point ID to look up plots, so we have to get the index of the plot for that
 * sublocation in the group of plots that share the same data point ID.
 * @param measurement The measurement.
 * @param panelMeasurement The panel measurement.
 * @param componentType The component type of the measurement.
 * @param sublocation The sublocation of the measurement.
 * @param assetType The asset type of the parent asset.
 */
export function findPlotPosition(
  measurementType: string,
  panelMeasurement: PanelMeasurement | undefined,
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  let position = undefined;

  switch (measurementType) {
    case "$TEV":
      position = getTevMeasurementPosition(componentType, sublocation, panelMeasurement?.TEVMeasurements, assetType);
      break;
    case "$UHF":
      position = getUhfMeasurementPosition(componentType, sublocation, panelMeasurement?.UHFMeasurements, assetType);
      break;
    case "$CABLE_PD":
      position = getCablePdMeasurementPosition(
        componentType,
        sublocation,
        panelMeasurement?.CablePDMeasurements,
        assetType,
      );
      break;
    case "$VPIS":
      position = getVpisMeasurementPosition(componentType, sublocation, panelMeasurement?.VPISMeasurements, assetType);
      break;
    case "$ULTRA":
      position = getUltraSonicMeasurementPosition(
        componentType,
        sublocation,
        panelMeasurement?.UltraSonicMeasurements,
        assetType,
      );
      break;
  }

  return position;
}

function getTevMeasurementPosition(
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  measurements: TEVMeasurement[] | undefined,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  if (!measurements || !canHavePlotPosition(componentType, measurements, assetType)) return undefined;

  // Find the measurement for the target component and sublocation first
  const sublocationMeasurement = measurements.find(
    (m) => m.ComponentType === componentType && m.SubLocation === sublocation,
  );
  if (sublocationMeasurement === undefined || sublocationMeasurement.EventsDataPoint === undefined) return undefined;

  // Find the index of the sublocation in the group, using the phase plot data point ID as the group identifier
  const phasePlotGroup = measurements.filter((m) => m.EventsDataPoint === sublocationMeasurement.EventsDataPoint);
  const measurementPosition = phasePlotGroup.findIndex((m) => m.SubLocation === sublocation);
  return measurementPosition === -1 ? undefined : measurementPosition;
}

function getUhfMeasurementPosition(
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  measurements: UHFMeasurement[] | undefined,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  if (!measurements || !canHavePlotPosition(componentType, measurements, assetType)) return undefined;

  // Find the measurement for the target component and sublocation first
  const sublocationMeasurement = measurements.find(
    (m) => m.ComponentType === componentType && m.SubLocation === sublocation,
  );
  if (sublocationMeasurement === undefined || sublocationMeasurement.EventsDataPoint === undefined) return undefined;

  // Find the index of the sublocation in the group, using the phase plot data point ID as the group identifier
  const phasePlotGroup = measurements.filter((m) => m.EventsDataPoint === sublocationMeasurement.EventsDataPoint);
  const measurementPosition = phasePlotGroup.findIndex((m) => m.SubLocation === sublocation);
  return measurementPosition === -1 ? undefined : measurementPosition;
}

function getCablePdMeasurementPosition(
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  measurements: CablePDMeasurement[] | undefined,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  if (!measurements || !canHavePlotPosition(componentType, measurements, assetType)) return undefined;

  // Find the measurement for the target component and sublocation first
  const sublocationMeasurement = measurements.find(
    (m) => m.ComponentType === componentType && m.SubLocation === sublocation,
  );
  if (sublocationMeasurement === undefined || sublocationMeasurement.EventsDataPoint === undefined) return undefined;

  // Find the index of the sublocation in the group, using the phase plot data point ID as the group identifier
  const phasePlotGroup = measurements.filter((m) => m.EventsDataPoint === sublocationMeasurement.EventsDataPoint);
  const measurementPosition = phasePlotGroup.findIndex((m) => m.SubLocation === sublocation);
  return measurementPosition === -1 ? undefined : measurementPosition;
}

function getVpisMeasurementPosition(
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  measurements: VPISMeasurement[] | undefined,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  if (!measurements || !canHavePlotPosition(componentType, measurements, assetType)) return undefined;

  // Find the measurement for the target component and sublocation first
  const sublocationMeasurement = measurements.find(
    (m) => m.ComponentType === componentType && m.SubLocation === sublocation,
  );
  if (sublocationMeasurement === undefined || sublocationMeasurement.EventsDataPoint === undefined) return undefined;

  // Find the index of the sublocation in the group, using the phase plot data point ID as the group identifier
  const phasePlotGroup = measurements.filter((m) => m.EventsDataPoint === sublocationMeasurement.EventsDataPoint);
  const measurementPosition = phasePlotGroup.findIndex((m) => m.SubLocation === sublocation);
  return measurementPosition === -1 ? undefined : measurementPosition;
}

function getUltraSonicMeasurementPosition(
  componentType: MeasurementComponent,
  sublocation: SubLocation,
  measurements: UltraSonicMeasurement[] | undefined,
  assetType?: ManagedSurveyAssetType,
): number | undefined {
  if (!measurements || !canHavePlotPosition(componentType, measurements, assetType)) return undefined;

  // Find the measurement for the target component and sublocation first
  const sublocationMeasurement = measurements.find(
    (m) => m.ComponentType === componentType && m.SubLocation === sublocation,
  );
  if (sublocationMeasurement === undefined || sublocationMeasurement.PhasePlotDataPoint === undefined) return undefined;

  // Find the index of the sublocation in the group, using the phase plot data point ID as the group identifier
  const phasePlotGroup = measurements.filter((m) => m.PhasePlotDataPoint === sublocationMeasurement.PhasePlotDataPoint);
  const measurementPosition = phasePlotGroup.findIndex((m) => m.SubLocation === sublocation);
  return measurementPosition === -1 ? undefined : measurementPosition;
}
